import { WSElementProps } from "@wingspanhq/fe-component-library";
import {
  SearchPayeeRowsFilterParameter,
  SearchRowEngagementStatusRollup,
  SearchRowRequirementStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesPayeeRows } from "@query";
import { Tabs } from "../../../../components/Tabs";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import {
  PAYEES_ALL_PATH,
  PAYEES_ARCHIVED_PATH,
  PAYEES_COMPLETE_PATH,
  PAYEES_INCOMPLETE_PATH,
  PAYEES_NOT_SIGNED_UP_PATH
} from "../../utils";

export const ContractorsTabs = [
  "all",
  "archived",
  "complete",
  "incomplete",
  "notSignedUp"
] as const;

export type ContractorTab = (typeof ContractorsTabs)[number];

/**
 * Per-tab v3 `searchPayeeRows` filter — the twin of the former v1
 * `ContractorsTabsFilters`, expressed on the denormalized row's engagement /
 * requirements status rollups.
 */
export const ContractorsTabsFilters: Record<
  ContractorTab,
  SearchPayeeRowsFilterParameter
> = {
  all: {
    engagementStatus: { noneOf: [SearchRowEngagementStatusRollup.INACTIVE] }
  },
  notSignedUp: {
    engagementStatus: { eq: SearchRowEngagementStatusRollup.NOT_SIGNED_UP }
  },
  incomplete: {
    requirementsStatus: { eq: SearchRowRequirementStatus.INCOMPLETE },
    engagementStatus: {
      anyOf: [
        SearchRowEngagementStatusRollup.NOT_ELIGIBLE,
        SearchRowEngagementStatusRollup.ELIGIBLE
      ]
    }
  },
  complete: {
    requirementsStatus: { eq: SearchRowRequirementStatus.COMPLETE }
  },
  archived: {
    engagementStatus: { eq: SearchRowEngagementStatusRollup.INACTIVE }
  }
};

export const DashboardTabs: React.FC<WSElementProps> = props => {
  const xWingspanAccount = useXWingspanExpansion();

  // Badge counts come from the list response's `pagination.totalSize`; a size-1
  // page keeps each count query cheap.
  const useTabCount = (filter: SearchPayeeRowsFilterParameter) =>
    queriesPayeeRows.searchPayeeRows.useQuery(
      { filter, page: { size: 1 }, xWingspanAccount },
      { select: list => list.pagination?.totalSize }
    );

  const queryAllPayees = useTabCount(ContractorsTabsFilters.all);
  const queryNotSignedUp = useTabCount(ContractorsTabsFilters.notSignedUp);
  const queryIncomplete = useTabCount(ContractorsTabsFilters.incomplete);
  const queryComplete = useTabCount(ContractorsTabsFilters.complete);
  const queryArchived = useTabCount(ContractorsTabsFilters.archived);

  return (
    <Tabs
      {...props}
      tabs={[
        {
          label: "Active",
          path: PAYEES_ALL_PATH,
          badge: queryAllPayees.data
        },
        {
          label: "Not signed up",
          path: PAYEES_NOT_SIGNED_UP_PATH,
          badge: queryNotSignedUp.data
        },
        {
          label: "Incomplete",
          path: PAYEES_INCOMPLETE_PATH,
          badge: queryIncomplete.data
        },
        {
          label: "Complete",
          path: PAYEES_COMPLETE_PATH,
          badge: queryComplete.data
        },
        {
          label: "Archived",
          path: PAYEES_ARCHIVED_PATH,
          badge: queryArchived.data
        }
      ]}
    />
  );
};
